import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Plus, PackageCheck, Pencil, Send, Check, X, Trash2 } from 'lucide-react';
import { PackageForm, blankPackage } from '@/components/sales/PackageForm';
import { ApprovalBadge, StatusTabs, RejectDialog, isApprover, userHasRole, apiError } from '@/components/sales/ApprovalBits';

const RowActions = ({ p, user, onEdit, onAct, onReject, onDelete }) => {
  const editable = ['draft', 'rejected'].includes(p.approval_status) && (p.created_by === user?.email || isApprover(user));
  const review = isApprover(user) && p.approval_status === 'pending_approval';
  const id = p.package_id;
  return (
    <div className="flex justify-end gap-1.5">
      {editable && <Button size="sm" variant="outline" onClick={() => onEdit(p)} data-testid={`pkg-edit-${id}`}><Pencil className="h-3.5 w-3.5" /></Button>}
      {editable && <Button size="sm" variant="outline" onClick={() => onAct(p, 'submit')} data-testid={`pkg-submit-${id}`}><Send className="h-3.5 w-3.5 mr-1" />Submit</Button>}
      {editable && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => onDelete(p)} data-testid={`pkg-delete-${id}`}><Trash2 className="h-3.5 w-3.5" /></Button>}
      {review && <Button size="sm" variant="outline" className="border-red-300 text-red-700" onClick={() => onReject(p)} data-testid={`pkg-reject-${id}`}><X className="h-3.5 w-3.5 mr-1" />Reject</Button>}
      {review && <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => onAct(p, 'approve')} data-testid={`pkg-approve-${id}`}><Check className="h-3.5 w-3.5 mr-1" />Approve</Button>}
    </div>
  );
};

export default function LcoPackages() {
  const { API, user } = useAuth();
  const approver = isApprover(user);
  const [pkgs, setPkgs] = useState([]);
  const [lcos, setLcos] = useState([]);
  const [tab, setTab] = useState(approver ? 'pending_approval' : 'all');
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [rejecting, setRejecting] = useState(null);
  const [saving, setSaving] = useState(false);
  const canCreate = userHasRole(user, ['admin', 'onboarding', 'lco_sales']);

  const load = useCallback(async () => {
    try {
      const [p, l] = await Promise.all([
        axios.get(`${API}/packages`),
        axios.get(`${API}/customers`, { params: { customer_type: 'lco' } }),
      ]);
      const lcoIds = new Set(l.data.map(c => c.customer_id));
      setLcos(l.data);
      setPkgs(p.data.filter(x => lcoIds.has(x.customer_id) || (x.approval_status && x.approval_status !== 'approved')));
    } catch (e) { toast.error(apiError(e, 'Failed to load packages')); }
  }, [API]);
  useEffect(() => { load(); }, [load]);

  const lcoName = useMemo(() => Object.fromEntries(lcos.map(c => [c.customer_id, c.company_name])), [lcos]);
  const statusOf = (p) => p.approval_status || 'approved';
  const counts = useMemo(() => pkgs.reduce((a, p) => ({ ...a, [statusOf(p)]: (a[statusOf(p)] || 0) + 1 }), { all: pkgs.length }), [pkgs]);
  const shown = tab === 'all' ? pkgs : pkgs.filter(p => statusOf(p) === tab);

  const save = async (payload) => {
    if (!payload.customer_id) return toast.error('Please select an LCO');
    setSaving(true);
    try {
      if (editing) {
        const { submit_for_approval, ...body } = payload;
        await axios.put(`${API}/packages/${editing.package_id}`, body);
        if (submit_for_approval) await axios.post(`${API}/packages/${editing.package_id}/submit`);
      } else {
        await axios.post(`${API}/packages`, payload);
      }
      toast.success(payload.submit_for_approval ? 'Package submitted for approval' : 'Package saved');
      setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(apiError(e, 'Failed to save package')); }
    finally { setSaving(false); }
  };

  const act = async (p, action, body) => {
    try {
      await axios.post(`${API}/packages/${p.package_id}/${action}`, body);
      toast.success(`${p.package_name}: ${action === 'submit' ? 'submitted' : action + 'd'}`);
      setRejecting(null); load();
    } catch (e) { toast.error(apiError(e, 'Action failed')); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete package ${p.package_name}?`)) return;
    try { await axios.delete(`${API}/packages/${p.package_id}`); toast.success('Package deleted'); load(); }
    catch (e) { toast.error(apiError(e, 'Delete failed')); }
  };

  return (
    <div className="space-y-6" data-testid="lco-packages-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="text-3xl font-semibold text-slate-900 tracking-tight">LCO Broadband Packages</h2>
          <p className="text-slate-600 mt-1">Packages go live for the LCO only after Admin/Manager approval.</p>
        </div>
        {canCreate && (
          <Button data-testid="new-lco-package-btn" className="bg-slate-900 hover:bg-slate-800 text-white" onClick={() => { setEditing(null); setOpen(true); }}>
            <Plus className="h-4 w-4 mr-2" /> New Package
          </Button>
        )}
      </div>

      <StatusTabs value={tab} onChange={setTab} counts={counts} prefix="pkg" />

      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Package</TableHead><TableHead>LCO</TableHead><TableHead>Speed</TableHead><TableHead>MRP</TableHead>
                <TableHead>LCO / ISP Share</TableHead><TableHead>Status</TableHead><TableHead>Created By</TableHead><TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {shown.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-12 text-slate-500" data-testid="pkg-empty">
                  <PackageCheck className="h-10 w-10 mx-auto text-slate-300 mb-2" />No packages here.
                </TableCell></TableRow>
              ) : shown.map(p => (
                <TableRow key={p.package_id} data-testid={`pkg-row-${p.package_id}`}>
                  <TableCell>
                    <div className="font-medium">{p.package_name}</div>
                    <div className="text-xs text-slate-500">{p.service_type}</div>
                    {statusOf(p) === 'rejected' && p.rejection_reason && <div className="text-xs text-red-600 mt-1" data-testid={`pkg-reason-${p.package_id}`}>Reason: {p.rejection_reason}</div>}
                  </TableCell>
                  <TableCell>{p.customer_name || lcoName[p.customer_id] || '—'}</TableCell>
                  <TableCell>{p.speed || '—'}</TableCell>
                  <TableCell>₹{Number(p.mrp || 0).toLocaleString('en-IN')}</TableCell>
                  <TableCell className="text-sm">{p.operator_share_percent ?? 0}% / {p.isp_share_percent ?? 100}%</TableCell>
                  <TableCell><ApprovalBadge status={statusOf(p)} testId={`pkg-status-${p.package_id}`} /></TableCell>
                  <TableCell className="text-xs text-slate-500">{p.created_by || '—'}</TableCell>
                  <TableCell><RowActions p={p} user={user} onEdit={(x) => { setEditing(x); setOpen(true); }} onAct={act} onReject={setRejecting} onDelete={remove} /></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditing(null); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="lco-package-dialog">
          <DialogHeader>
            <DialogTitle className="text-2xl">{editing ? 'Edit Package' : 'New LCO Package'}</DialogTitle>
            <DialogDescription>Internet broadband package for an LCO&apos;s subscribers.</DialogDescription>
          </DialogHeader>
          {open && <PackageForm initial={editing ? { ...blankPackage(), ...editing } : blankPackage()} lcos={lcos} onSubmit={save} saving={saving} isEdit={!!editing} />}
        </DialogContent>
      </Dialog>

      <RejectDialog open={!!rejecting} prefix="pkg" title={`Reject ${rejecting?.package_name || ''}`}
        onClose={() => setRejecting(null)} onConfirm={(reason) => act(rejecting, 'reject', { reason })} />
    </div>
  );
}
